/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import { invalidateAuditLogQueries } from "../../utils/invalidateAuditLogQueries";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  Drawer,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  Divider,
  Paper,
  Popover,
  Radio,
  RadioGroup,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState, useContext, useMemo } from "react";
import { NavigableBreadcrumbLink } from "../../components/page_breadcrumbs/navigable_breadcrumb_link";
import { selectBaseStyles, viewTypographyStyles } from "../reports/styles";
import { FilterContainer } from "../../components/filter_container";
import { UpdateButton } from "../../components/update_button";
import { uploadJobs } from "../../api/jobServices.js";
import { Loader } from "../../components/loader";
import { IndexTable } from "../../components/index_table";
import camelize from "../../utils/camelize.js";
import { StyledNavLink, DepartmentBadge } from "./styles";
import {
  StyledStatusButton,
  StatusMenuList,
  statusPopoverPaperStyle,
  statusMenuItemStyle,
  ClassificationActionButton,
} from "../configured_discounts/styles.jsx";
import { DiscountClassificationModal } from "../../components/discount_classification_modal";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import api from "../../api/index.js";
import authenticatedFetch from "../../api/authenticatedFetch";
import { ActionContainer } from "../../components/action_container";
import CachedIcon from "@mui/icons-material/Cached";
import { AbilityContext } from "../../contexts/abilityContext";
import {
  StyledBreadCrumbContainer,
  StyledLink,
} from "../user_management/styles";
import { StyledInputLabel } from "../restaurant/styles.jsx";
import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { strictCanScope } from "../../utils/caslStrict.js";
import { useNotificationContext } from "../../contexts/notificationContext.jsx";
import { LoadingSpinner } from "../../components/loading_spinner/index.jsx";
import DropdownSearchBar from "../../components/dorpdown_searchbar/index.jsx";
import { DropdownInfiniteLoader } from "../../components/dropdown_infinite_loader/index.jsx";
import { useLocation, useNavigate } from "react-router-dom";
import { SelectedMenuItem } from '../../components/selected_menu_item/index.jsx'
import { buildQueryParams } from "../../utils/buildQueryParams.js";
import { useDebounce } from "../../utils/useDebounce.js";
import { containsText, matchesNormalizedSearch } from "../../utils/normalizeSearchTerm";
import dayjs from "dayjs";
import {
  StyledAddButton,
  StyledButtonContainer,
  StyledCancelButton,
  StyledCreateButton,
  StyledCreateTitle,
  StyledFormFieldText,
  StyledSelect,
  StyledTextField
} from "../network_guests/styles.jsx";
import AddIcon from "@mui/icons-material/Add";
import { InfoCircleOutlined } from "@ant-design/icons";
import { formatDate } from "../../utils/formatDate.js";
import { capitalize } from 'lodash'
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import { useSelector } from "react-redux";
import { checkAdmin } from "../../utils/checkAdmin.js";
import DeleteIcon from "@mui/icons-material/Delete";
import { PerPage } from "../../constants/perPage.js";
import { useInfiniteScrollObserver } from "../../hooks/useInfiniteScrollObserver.js";
import {
  getDropdownNextPageParam,
  mergeUniqueByKey,
  normalizeRestaurant,
  normalizeRestaurantGroup,
} from "../../utils/dropdownInfiniteQuery.js";
import { ConfirmationDialog } from "../../components/confirmation_dialog/index.jsx";
import CheckIcon from "@mui/icons-material/Check";
import { StyledMenuItem } from "../../components/styled_menu_item/index.jsx";
import { SOURCE_OF_TRUTH_FETCH_OPTIONS, DEPARTMENT_OPTIONS, DEPARTMENT_LABELS } from "../../constants/filterOptions.js";
import ClearIcon from '@mui/icons-material/Clear';
import { ResetButton } from "../../components/reset_button";
import SearchIcon from '@mui/icons-material/Search';
import { StyledSearchBar } from "../employee/styles.js";
import { Query_Keys } from "../../lib/queryKeys.js";
import { useOtMutation } from "../../hooks/mutations/useOtMutation.js";
import { jobMutationKeyGroups } from "../../hooks/mutations/queryKeyGroups/jobMutationKeys.js";
import { useParentContextHydration } from "../../hooks/useParentContextHydration.js";
import { useQueryClient } from "@tanstack/react-query";
import { FetchDataModal } from "../../components/fetch_data_modal/index"
import { formatHelper } from "../../utils/formatHelper.js";
import { bifurcateRestaurantData, hasAnyConfiguredSource } from "../../utils/bifurcateConfiguredRestaurantData.js";

const drawerWidth = 470;
const JOB_TITLE_UPDATE_CONFIRMATION_MESSAGE = "Changing this job title will not update existing employees. It will apply only to new assignments.";

// Unmapped is the system default and cannot be selected again once a job is mapped to FOH/BOH.
const DEPARTMENT_ACTIONS = DEPARTMENT_OPTIONS.filter(({ value }) => value !== 'unmapped');

const DEPARTMENT_ERRORS = {
  department: 'Please select a valid department',
};

const EMPTY_FILTERS = {
  restaurant_ids: [],
  restaurant_group_id: '',
  source_of_truth: '',
};

const flattenDropdownPages = (data) => data.pages.flat();

const resolveLegacyRestaurantFilter = async (restaurant) => {
  if (!restaurant?.name) {
    return null;
  }

  if (restaurant?.id) {
    return restaurant;
  }

  try {
    const response = await api.get('/v1/admin/dropdowns/restaurants_list', {
      params: {
        page: 1,
        per_page: PerPage,
        search: restaurant.name,
      },
    });

    const normalizedOptions = (response?.data || []).map(normalizeRestaurant);
    const matchedRestaurant = normalizedOptions.find((option) => {
      return matchesNormalizedSearch(option?.name, restaurant.name);
    });

    return matchedRestaurant || { ...restaurant, id: null };
  } catch (error) {
    return { ...restaurant, id: null };
  }
};

const mergeOptionsIfChanged = (currentOptions = [], nextOptions = [], key, comparedFields = []) => {
  const mergedOptions = mergeUniqueByKey(currentOptions, nextOptions, key);

  if (mergedOptions.length !== currentOptions.length) {
    return mergedOptions;
  }

  const hasChanges = mergedOptions.some((option, index) => {
    const currentOption = currentOptions[index];

    if (currentOption?.[key] !== option?.[key]) {
      return true;
    }

    return comparedFields.some((field) => currentOption?.[field] !== option?.[field]);
  });

  return hasChanges ? mergedOptions : currentOptions;
};

export const Jobs = () => {
  const singleRestaurantAutoApplied = React.useRef(false)
  const singleRestaurantGroupAutoApplied = React.useRef(false)
  // Breadcrumb return trip (job_post -> back to Jobs) restores a prior appliedFilters via
  // parentContext, which may legitimately have no restaurant/restaurant-group selected
  // (e.g. department-only or unfiltered). hasAppliedRestaurantFilter below treats that
  // restored context as satisfying the "a filter has been applied" requirement too.
  const hydratedFromParentContext = React.useRef(false)
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [restaurantId, setRestaurantId] = useState(null);
  const location = useLocation()
  const [filtersChanged, setFiltersChanged] = useState(false);
  const initialFilters = {
    restaurant_ids: location?.state?.restaurantId || [],
    restaurant_group_id: location?.state?.restaurantGroupId || '',
    source_of_truth: location?.state?.sourceOfTruth || '',
  }
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [fileUploadOpen, setFileUploadOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [restaurantSelected, setRestaurantSelected] = useState(false)
  const { ability } = useContext(AbilityContext);
  const [fetchPermission, setFetchPermission] = useState(false)
  const showNotification = useNotificationContext()
  const [isLoading, setIsLoading] = React.useState(false)
  const [restaurantSearch, setRestaurantSearch] = useState("");
  const [restaurantGroupSearch, setRestaurantGroupSearch] = useState("")
  const navigate = useNavigate()
  const navigatedRestaurant = location?.state?.restaurant
  const [csvLoading, setCsvLoading] = useState(false)
  const [createInstancePermission, setCreateInstancePermission] = useState(false);
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false)
  const [editDrawerOpen, setEditDrawerOpen] = useState(false)
  const [createRestaurantSearch, setCreateRestaurantSearch] = useState('')
  const [createRestaurantGroupSearch, setCreateRestaurantGroupSearch] = useState('')
  const [createRestaurantGroupIds, setCreateRestaurantGroupIds] = useState([])
  const [errors, setErrors] = useState({})
  const [updateErrors, setUpdateErrors] = useState({})
  const [fetchedJob, setFetchedJob] = useState({})
  const [deleteTargetJob, setDeleteTargetJob] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [jobTitleConfirmationOpen, setJobTitleConfirmationOpen] = useState(false)
  const [departmentMenuAnchorEl, setDepartmentMenuAnchorEl] = useState(null)
  const [selectedDepartmentRecord, setSelectedDepartmentRecord] = useState(null)
  const [rowDepartmentLoadingId, setRowDepartmentLoadingId] = useState(null)
  const [selectedIds, setSelectedIds] = useState([])
  const [bulkDepartmentOpen, setBulkDepartmentOpen] = useState(false)
  const [bulkDepartmentAction, setBulkDepartmentAction] = useState('')
  const [bulkDepartmentLoading, setBulkDepartmentLoading] = useState(false)
  const { admin } = useSelector((state) => state.admin);
  const superAdmin = checkAdmin(admin?.formatted_roles)
  const [deleteJobDialogOpen, setDeleteJobDialogOpen] = useState(false)
  const [sourceOfTruthSearch, setSourceOfTruthSearch] = useState("")
  const [departmentSearch, setDepartmentSearch] = useState("")
  const [search, setSearch] = useState("")
  const [applyFilters, setApplyFilters] = useState(false)
  const [showResetAlert, setShowResetAlert] = useState(false)
  const debouncedSearchTerm = useDebounce(search, 300);
  const debouncedRestaurantSearch = useDebounce(restaurantSearch.trim(), 300);
  const debouncedRestaurantGroupSearch = useDebounce(restaurantGroupSearch.trim(), 300);
  const debouncedCreateRestaurantSearch = useDebounce(createRestaurantSearch.trim(), 300);
  const debouncedCreateRestaurantGroupSearch = useDebounce(createRestaurantGroupSearch.trim(), 300);
  const [restaurantOptionsCache, setRestaurantOptionsCache] = useState(() => (
    navigatedRestaurant?.id && navigatedRestaurant?.name
      ? [{ id: navigatedRestaurant.id, name: navigatedRestaurant.name }]
      : []
  ))
  const [restaurantGroupOptionsCache, setRestaurantGroupOptionsCache] = useState([])
  const [createRestaurantOptionsCache, setCreateRestaurantOptionsCache] = useState([])
  const queryClient = useQueryClient();
  const [newJob, setNewJob] = useState({
    title: null,
    code: null,
    pay_basis: null,
    default_wage: null,
    tipped: null,
    cashier: null,
    driver: null,
    restaurant_id: ''
  })
  const [openFetchDataModal, setOpenFetchDataModal] = useState(false)
  const [enableFetching, setEnableFetching] = useState(false)
  const [fetchSource, setFetchSource] = useState({
    toast: false,
    seven_shifts: false,
  })
  const defaultCreatePermission = strictCanScope(
    ability,
    "create_network_guest",
    "Restaurant"
  );

  useParentContextHydration({
    ability,
    setters: {
      setFilters,
      setAppliedFilters,
      setSearch,
      setPage,
      setItemsPerPage,
      setSortBy,
      setSortOrder,
      setApplyFilters,
    },
    defaultSortBy: 'created_at',
    defaultSortOrder: 'desc',
    permissionResolver: (parentCtx, currentAbility) => {
      hydratedFromParentContext.current = true
      if (parentCtx.filters?.restaurant_ids) {
        const ids = Array.isArray(parentCtx.filters.restaurant_ids)
          ? parentCtx.filters.restaurant_ids
          : [parentCtx.filters.restaurant_ids]

        const fetchAllowed = ids.some((id) => strictCanScope(currentAbility, 'fetch_and_imports', 'Restaurant', `${id}`));
        const createAllowed = ids.some((id) => strictCanScope(currentAbility, 'create_network_guest', 'Restaurant', `${id}`));
        setFetchPermission(Boolean(fetchAllowed));
        setCreateInstancePermission(Boolean(createAllowed));
      }
    },
    legacyHandler: async (currentAbility) => {
      if (!navigatedRestaurant?.name) {
        return;
      }

      const resolvedRestaurant = await resolveLegacyRestaurantFilter(navigatedRestaurant);

      if (!resolvedRestaurant?.id) {
        return;
      }

      setFetchPermission(strictCanScope(currentAbility, 'fetch_and_imports', 'Restaurant', `${resolvedRestaurant?.id}`))
      setRestaurantOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, [{
        id: resolvedRestaurant.id,
        name: resolvedRestaurant.name,
      }], 'id'));
      const newFilters = {
        ...filters,
        restaurant_ids: [resolvedRestaurant.id]
      };
      setFilters(newFilters);
      setAppliedFilters(newFilters);
      setApplyFilters(true);
      setCreateInstancePermission(strictCanScope(currentAbility, 'create_network_guest', 'Restaurant', `${resolvedRestaurant?.id}`))
    },
    dependencies: [navigatedRestaurant]
  });

  const fetchRestaurant = async ({ pageParam = 1 }) => {
    const res = await api.get('/v1/admin/dropdowns/restaurants_list', {
      params: {
        restaurant_group_ids: filters?.restaurant_group_id,
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantSearch || undefined,
      }
    })
    return res?.data?.map(normalizeRestaurant) || []
  }

  const {
    data: restaurants = [],
    isFetching: restaurantFetching,
    isFetchingNextPage: restaurantsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurants_dropdown({
      restaurant_group_id: filters?.restaurant_group_id,
      search: debouncedRestaurantSearch,
      source: 'jobs_filter',
    }),
    queryFn: fetchRestaurant,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: flattenDropdownPages,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  const { rootRef: restaurantMenuRef, targetRef: restaurantLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
    isFetchingNextPage: restaurantsFetchingNextPage,
  })

  const fetchEmployeeJobs = async () => {
    const res = await api.get('/v1/admin/jobs', {
      params: {
        per_page: itemsPerPage,
        page: page,
        sort_by: sortBy,
        sort_order: sortOrder,
        search: debouncedSearchTerm,
        ...appliedFilters
      }
    })
    return { data: res?.data, pageCount: res?.headers["total-count"] }
  }

  const hasAppliedRestaurantFilter = (Boolean(appliedFilters?.restaurant_group_id) ||
    Boolean(appliedFilters?.restaurant_ids?.length > 0)) || hydratedFromParentContext.current

  const { data: jobs, isFetching } = useQuery({
    queryKey: Query_Keys.jobs({
      queryFilter: appliedFilters,
      page: page,
      itemsPerPage: itemsPerPage,
      sortBy: sortBy,
      sortOrder: sortOrder,
      search: debouncedSearchTerm,
    }),
    queryFn: fetchEmployeeJobs,
    staleTime: 10 * 60 * 1000,
    enabled: hasAppliedRestaurantFilter,
    placeholderData: keepPreviousData
  })

  const fetchConfiguredRestaurants = async () => {
    const res = await api.get('/v1/admin/restaurants/check_configuration_source', { params: {
      restaurant_ids: filters?.restaurant_ids
    }})

    return res?.data
  }

  const { data: configuredRestaurants, isFetching: configuredRestaurantsFetching } = useQuery({
    queryKey: Query_Keys.configured_restaurants({
      restaurant_ids: filters?.restaurant_ids
    }),
    queryFn: fetchConfiguredRestaurants,
    staleTime: 10 * 60 * 1000,
    enabled: enableFetching,
  })

  const fetchCreateRestaurant = async ({ pageParam = 1 }) => {
    const response = await api.get('/v1/admin/dropdowns/network_guest_create_restaurant_list', {
      params: {
        restaurant_group_id: createRestaurantGroupIds,
        page: pageParam,
        per_page: PerPage,
        search: debouncedCreateRestaurantSearch || undefined,
      }
    })

    return response?.data?.map((restaurant) => ({
      value: restaurant?.value ?? restaurant?.id,
      label: restaurant?.label ?? restaurant?.name,
    })) || []
  }

  const {
    data: createRestaurantList = [],
    isFetching: createRestaurantFetching,
    isFetchingNextPage: createRestaurantsFetchingNextPage,
    fetchNextPage: fetchNextCreateRestaurantsPage,
    hasNextPage: hasNextCreateRestaurantsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.create_restaurant_dropdown({
      restaurant_group_id: createRestaurantGroupIds,
      search: debouncedCreateRestaurantSearch,
      source: 'jobs_create',
    }),
    queryFn: fetchCreateRestaurant,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: flattenDropdownPages,
    staleTime: 10 * 60 * 1000,
    enabled: Boolean(createRestaurantGroupIds.length > 0) || true,
    placeholderData: keepPreviousData,
  })

  const { rootRef: createRestaurantMenuRef, targetRef: createRestaurantLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextCreateRestaurantsPage,
    hasNextPage: hasNextCreateRestaurantsPage,
    isFetchingNextPage: createRestaurantsFetchingNextPage,
  })

  const fetchRestaurantGroupList = async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/restaurant_groups_list", {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantGroupSearch || undefined,
      },
    });

    return response?.data?.map(normalizeRestaurantGroup) || [];
  }

  const {
    data: restaurantGroups = [],
    isFetching: restaurantGroupFetching,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_groups_dropdown({
      search: debouncedRestaurantGroupSearch,
      source: 'jobs_filter',
    }),
    queryFn: fetchRestaurantGroupList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: flattenDropdownPages,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { rootRef: restaurantGroupMenuRef, targetRef: restaurantGroupLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
  })

  const fetchCreateRestaurantGroupList = async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/restaurant_groups_list", {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedCreateRestaurantGroupSearch || undefined,
      },
    });

    return response?.data?.map(normalizeRestaurantGroup) || [];
  }

  const {
    data: createRestaurantGroupsList = [],
    isFetching: createRestaurantGroupFetching,
    isFetchingNextPage: createRestaurantGroupsFetchingNextPage,
    fetchNextPage: fetchNextCreateRestaurantGroupsPage,
    hasNextPage: hasNextCreateRestaurantGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_groups_dropdown({
      search: debouncedCreateRestaurantGroupSearch,
      source: 'jobs_create',
    }),
    queryFn: fetchCreateRestaurantGroupList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: flattenDropdownPages,
    staleTime: 10 * 60 * 1000,
    enabled: createDrawerOpen,
    placeholderData: keepPreviousData,
  })

  const { rootRef: createRestaurantGroupMenuRef, targetRef: createRestaurantGroupLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextCreateRestaurantGroupsPage,
    hasNextPage: hasNextCreateRestaurantGroupsPage,
    isFetchingNextPage: createRestaurantGroupsFetchingNextPage,
  })

  React.useEffect(() => {
    setRestaurantOptionsCache((currentOptions) => mergeOptionsIfChanged(currentOptions, restaurants, 'id', ['name']))
  }, [restaurants])

  React.useEffect(() => {
    if (!navigatedRestaurant?.id || !navigatedRestaurant?.name) {
      return
    }

    setRestaurantOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, [{
      id: navigatedRestaurant.id,
      name: navigatedRestaurant.name,
    }], 'id'))
  }, [navigatedRestaurant?.id, navigatedRestaurant?.name])

  React.useEffect(() => {
    setRestaurantGroupOptionsCache((currentOptions) => mergeOptionsIfChanged(currentOptions, restaurantGroups, 'label', ['value']))
  }, [restaurantGroups])

  React.useEffect(() => {
    setRestaurantGroupOptionsCache((currentOptions) => mergeOptionsIfChanged(currentOptions, createRestaurantGroupsList, 'label', ['value']))
  }, [createRestaurantGroupsList])

  React.useEffect(() => {
    setCreateRestaurantOptionsCache((currentOptions) => mergeOptionsIfChanged(currentOptions, createRestaurantList, 'value', ['label']))
  }, [createRestaurantList])

  const hasSingleAvailableRestaurant = !debouncedRestaurantSearch && restaurants.length === 1

  React.useEffect(() => {
    if (filters?.restaurant_ids?.length > 0 || singleRestaurantAutoApplied.current || !hasSingleAvailableRestaurant) {
      return
    }

    singleRestaurantAutoApplied.current = true
    setFetchPermission(strictCanScope(ability, 'fetch_and_imports', 'Restaurant', `${restaurants?.[0]?.id}`))
    setFilters((currentFilters) => ({ ...currentFilters, restaurant_ids: [restaurants?.[0]?.id] }));
    setCreateInstancePermission(strictCanScope(ability, 'create_network_guest', 'Restaurant', `${restaurants?.[0]?.id}`))
  }, [ability, filters?.restaurant_ids, hasSingleAvailableRestaurant, restaurants])

  const hasSingleAvailableRestaurantGroup = !debouncedRestaurantGroupSearch && restaurantGroups.length === 1

  React.useEffect(() => {
    if (filters?.restaurant_group_id || singleRestaurantGroupAutoApplied.current || !hasSingleAvailableRestaurantGroup) {
      return
    }

    singleRestaurantGroupAutoApplied.current = true
    setFilters((currentFilters) => ({ ...currentFilters, restaurant_group_id: restaurantGroups?.[0]?.label }))
    setCreateRestaurantGroupIds([restaurantGroups?.[0]?.label])
  }, [filters?.restaurant_group_id, hasSingleAvailableRestaurantGroup, restaurantGroups])

  React.useEffect(() => {
    if (!enableFetching) return

    if (configuredRestaurantsFetching) return

    if(hasAnyConfiguredSource(configuredRestaurants)) {
      setOpenFetchDataModal(true)
    }
    else {
      showNotification('No toast or seven shifts configuration found for selected restaurants', 'error')
      setEnableFetching(false)
    }
  }, [enableFetching, configuredRestaurants, configuredRestaurantsFetching])

  const getRestaurantGroupName = (id) => {
    const restaurantGroup = restaurantGroupOptionsCache?.find((res) => `${res?.label}` === `${id}`)
    return restaurantGroup?.value ? restaurantGroup?.value : ''
  }

  const getCreateRestaurantGroupName = (id) => {
    const restaurantGroup = restaurantGroupOptionsCache?.find((res) => `${res?.label}` === `${id}`)
    return restaurantGroup?.value ? restaurantGroup?.value : ''
  }

  const getPermission = (job, action) => {
    if (superAdmin?.role === 'admin') {
      return true
    }

    return strictCanScope(ability, action, 'Restaurant', `${job?.restaurant?.id}`)
  }

  const { mutateAsync: fetchJobsMutation } = useOtMutation({
    mutationFn: async (payload) => await api.post('/v1/admin/jobs/fetch_jobs', payload),
    invalidateKeys: [...jobMutationKeyGroups.fetch],
    successMessage: ({ variables: parameters }) => `Jobs imported successfully for ${getRestaurantName(parameters?.restaurant_id)}`,
    errorMessage: ({ variables: parameters }) => `Failed to fetch jobs for ${getRestaurantName(parameters?.restaurant_id)}`,
    onSuccess: () => {
      setApplyFilters(true)
    },
    onSettled: () => {
      invalidateAuditLogQueries(queryClient);
    }
  });

  const fetchJobsFromToast = async () => {
    const restaurants = [
      ...(fetchSource?.toast ? toastConfiguredRestaurants : []),
      ...(fetchSource?.seven_shifts ? sevenShiftConfiguredRestaurants : []),
    ]

    if (restaurants?.length === 0) {
      showNotification('Please select at least one restaurant', 'error')
      return
    }

    setIsLoading(true)

    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

    try {
      for (const res of restaurants) {
        if (superAdmin?.role === 'admin' || strictCanScope(ability, 'fetch_and_imports', 'Restaurant', `${res?.id}`)) {
          await delay(3500)
          await fetchJobsMutation({ restaurant_id: res?.id, api: true, provider: res?.provider  })
        }
      }
    } catch {
      // Handled in onError
    } finally {
      setIsLoading(false)
    }
  };

  const filteredSourceOfTruth = SOURCE_OF_TRUTH_FETCH_OPTIONS?.filter((source_of_truth) => {
    return matchesNormalizedSearch(source_of_truth?.label, sourceOfTruthSearch);
  })

  const filteredDepartments = DEPARTMENT_OPTIONS?.filter((department) => {
    return matchesNormalizedSearch(department?.label, departmentSearch);
  })

  const selectedRestaurantGroupOption = restaurantGroupOptionsCache?.find(
    (restaurantGroup) => `${restaurantGroup?.label}` === `${filters?.restaurant_group_id}`
  )
  const displayedRestaurantGroups = restaurantGroupSearch.trim()
    ? restaurantGroups?.filter((restaurantGroup) => matchesNormalizedSearch(restaurantGroup?.value, restaurantGroupSearch))
    : selectedRestaurantGroupOption
      ? mergeUniqueByKey(restaurantGroups, [selectedRestaurantGroupOption], 'label')
      : restaurantGroups

  const selectedRestaurantOption = restaurantOptionsCache?.filter((restaurant) => (
    filters?.restaurant_ids?.some((id) => `${restaurant?.id}` === `${id}`)
  ))

  const displayedRestaurants = restaurantSearch.trim()
    ? restaurants?.filter((restaurant) => matchesNormalizedSearch(restaurant?.name, restaurantSearch))
    : selectedRestaurantOption && selectedRestaurantOption.length > 0
      ? mergeUniqueByKey(restaurants, selectedRestaurantOption, 'id')
      : restaurants

  const selectedCreateRestaurantGroups = restaurantGroupOptionsCache?.filter((restaurantGroup) => (
    createRestaurantGroupIds?.some((selectedId) => `${selectedId}` === `${restaurantGroup?.label}`)
  ))
  const displayedCreateRestaurantGroups = createRestaurantGroupSearch.trim()
    ? createRestaurantGroupsList?.filter((restaurantGroup) => matchesNormalizedSearch(restaurantGroup?.value, createRestaurantGroupSearch))
    : mergeUniqueByKey(
      createRestaurantGroupsList,
      selectedCreateRestaurantGroups,
      'label'
    )

  const selectedCreateRestaurantOption = createRestaurantOptionsCache?.find(
    (restaurant) => `${restaurant?.value}` === `${newJob?.restaurant_id}`
  )
  const displayedCreateRestaurants = createRestaurantSearch.trim()
    ? createRestaurantList?.filter((restaurant) => matchesNormalizedSearch(restaurant?.label, createRestaurantSearch))
    : selectedCreateRestaurantOption
      ? mergeUniqueByKey(createRestaurantList, [selectedCreateRestaurantOption], 'value')
      : createRestaurantList

  const handleSearch = (searchQuery) => {
    setSearch(searchQuery)
    setPage(1)
    setSelectedIds([])
  }

  const handleSourceOfTruthChange = (event) => {
    setFiltersChanged(true)
    setFilters(prev => ({ ...prev, source_of_truth: event.target.value }))
  };

  const handleSourceOfTruthClear = () => {
    setFilters(prev => ({ ...prev, source_of_truth: '' }))
    setFiltersChanged(true);
  }

  const handleDepartmentChange = (event) => {
    setFiltersChanged(true)
    setFilters(prev => ({ ...prev, department: event.target.value }))
  };

  const handleDepartmentClear = () => {
    setFilters(prev => ({ ...prev, department: '' }))
    setFiltersChanged(true);
  }

  const handleExportCsv = async () => {
    try {
      setCsvLoading(true);
      const query = buildQueryParams({ ...filters, search: debouncedSearchTerm })
      const url = `${process.env.REACT_APP_API_BASE_URL}/v1/admin/csv_reports/export?report=jobs&${query}`;

      const { response } = await authenticatedFetch(url, {
        method: 'GET',
        defaultErrorMessage: 'Failed to download CSV'
      });

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = downloadUrl;
      let date = new Date
      let filename = `jobs_${dayjs(date).format('YYYY-MM-DD')}.csv`

      a.download = filename;
      a.click();

      window.URL.revokeObjectURL(downloadUrl);

      showNotification('CSV downloaded successfully', 'success');
    } catch (error) {
      console.error('CSV download error:', error);
      showNotification(error?.message || 'Failed to download CSV', 'error');
    } finally {
      setCsvLoading(false);
    }
  }

  const handleCreateDrawerClose = () => {
    setCreateDrawerOpen(false)
    setNewJob({
      title: null,
      code: null,
      pay_basis: null,
      default_wage: null,
      tipped: null,
      cashier: null,
      driver: null,
      restaurant_id: ''
    });
    setErrors({});
    setCreateRestaurantGroupIds([])
  }

  const handleEditDrawerClose = () => {
    setEditDrawerOpen(false)
    setFetchedJob({})
    setUpdateErrors({})
    setJobTitleConfirmationOpen(false)
  }

  const { mutateAsync: addJobMutation } = useOtMutation({
    mutationFn: async (payload) => await api.post("/v1/admin/jobs", payload),
    invalidateKeys: [...jobMutationKeyGroups.create],
    successMessage: () => "Job has been added successfully",
    onSuccess: () => {
      handleCreateDrawerClose()
    },
    onError: (error) => {
      setErrors(error?.response?.data?.errors || {});
    },
    onSettled: () => setLoading(false)
  });

  const handleAddJob = async () => {
    if (!newJob?.restaurant_id) {
      setErrors(prev => ({ ...prev, restaurant_id: 'Please select a restaurant' }))
      return
    }
    setLoading(true);

    try {
      await addJobMutation({
        restaurant_id: newJob?.restaurant_id,
        job: newJob
      });
    } catch {
      // Handled in onError
    }
  }

  const { mutateAsync: updateJobMutation } = useOtMutation({
    mutationFn: async (payload) => await api.put(`/v1/admin/jobs/${fetchedJob?.id}`, payload),
    invalidateKeys: [...jobMutationKeyGroups.update],
    successMessage: () => "Job has been updated successfully",
    onSuccess: () => {
      handleEditDrawerClose()
    },
    onError: (error) => {
      setUpdateErrors(error?.response?.data?.errors || {});
    },
    onSettled: () => setLoading(false)
  });

  const { mutateAsync: deleteJobMutation } = useOtMutation({
    mutationFn: async (params) => await api.delete(`/v1/admin/jobs/${deleteTargetJob?.id}`, { params }),
    invalidateKeys: [...jobMutationKeyGroups.delete],
    successMessage: () => "Job has been deleted successfully",
    errorMessage: ({ error }) => {
      const errors = error?.response?.data?.errors;
      return errors?.base?.[0] || errors?.job_id?.[0] || 'Failed to delete job';
    },
    onSuccess: () => {
      setDeleteJobDialogOpen(false)
      if (deleteTargetJob?.id === fetchedJob?.id && editDrawerOpen) {
        handleEditDrawerClose();
      }
    },
    onError: () => {
      setDeleteJobDialogOpen(false);
    },
    onSettled: () => setDeleteLoading(false)
  });

  const getDepartmentErrorMessage = (errors = {}) => {
    const errorKey = Object.keys(errors)?.[0]
    return DEPARTMENT_ERRORS[errorKey] || 'Failed to update the department'
  }

  const { mutateAsync: updateJobDepartmentMutation } = useOtMutation({
    mutationFn: async ({ id, department }) => await api.patch(`/v1/admin/jobs/${id}/department`, { department }),
    invalidateKeys: [...jobMutationKeyGroups.updateDepartment],
    successMessage: () => "Job department updated successfully",
    errorMessage: ({ error }) => getDepartmentErrorMessage(error?.response?.data?.errors),
  });

  const submitJobUpdate = async () => {
    setJobTitleConfirmationOpen(false)
    setLoading(true);

    try {
      await updateJobMutation({
        job: {
          title: fetchedJob?.title,
          code: fetchedJob?.code,
          pay_basis: fetchedJob?.pay_basis,
          default_wage: fetchedJob?.default_wage,
          tipped: fetchedJob?.tipped,
          cashier: fetchedJob?.cashier,
          driver: fetchedJob?.driver
        }
      });
    } catch {
      // Handled in onError
    }
  }

  const shouldConfirmJobTitleUpdate = () => {
    return fetchedJob?.assigned_to_employees && fetchedJob?.original_title !== fetchedJob?.title
  }

  const handleUpdateJob = async () => {
    if (shouldConfirmJobTitleUpdate()) {
      setJobTitleConfirmationOpen(true)
      return
    }

    await submitJobUpdate()
  }

  const handleDeleteJob = async () => {
    setDeleteLoading(true);

    try {
      await deleteJobMutation({
        restaurant_id: deleteTargetJob?.restaurant?.id
      });
    } catch {
      // Handled in onError
    }
  }

  const handleDepartmentMenuOpen = (event, record) => {
    if (rowDepartmentLoadingId) return

    setDepartmentMenuAnchorEl(event.currentTarget)
    setSelectedDepartmentRecord(record)
  }

  const handleDepartmentMenuClose = () => {
    if (rowDepartmentLoadingId) return

    setDepartmentMenuAnchorEl(null)
    setSelectedDepartmentRecord(null)
  }

  const updateJobDepartment = async (record, nextDepartment) => {
    if (!record?.id || record?.department === nextDepartment) return

    // Close before awaiting: the table unmounts while refetching, which detaches
    // the popover anchor and makes MUI fall back to positioning it at (0, 0).
    handleDepartmentMenuClose()
    setRowDepartmentLoadingId(record.id)

    try {
      await updateJobDepartmentMutation({ id: record.id, department: nextDepartment });
    } catch {
      // Handled in onError
    } finally {
      setRowDepartmentLoadingId(null)
    }
  }

  const canEditDepartment = (record) => getPermission(record, 'update_network_guest')

  const handleBulkDepartmentOpen = () => {
    setBulkDepartmentOpen(true)
  }

  const handleBulkDepartmentClose = () => {
    setBulkDepartmentOpen(false)
    setBulkDepartmentAction('')
  }

  const getBulkDepartmentErrorMessage = (errors = {}) => {
    const [firstMessage] = Object.values(errors).flat()
    return firstMessage || 'Failed to update the departments'
  }

  const getBulkDepartmentSuccessMessage = ({ updated_count: updated = 0, unchanged_count: unchanged = 0, skipped_count: skipped = 0 }) => {
    if (!updated) {
      return unchanged ? 'Selected jobs already use this department' : 'No job departments were updated'
    }

    const summary = `${updated} ${updated === 1 ? 'job' : 'jobs'} updated successfully`
    return skipped ? `${summary}, ${skipped} skipped` : summary
  }

  const { mutateAsync: bulkDepartmentUpdateMutation } = useOtMutation({
    mutationFn: async (payload) => await api.post('/v1/admin/jobs/bulk_department_update', payload),
    invalidateKeys: [...jobMutationKeyGroups.updateDepartment],
    successMessage: ({ data }) => getBulkDepartmentSuccessMessage(data?.data || {}),
    errorMessage: ({ error }) => getBulkDepartmentErrorMessage(error?.response?.data?.errors),
    onSuccess: () => {
      setSelectedIds([])
      handleBulkDepartmentClose()
    },
  });

  const updateJobDepartments = async () => {
    if (selectedIds.length === 0) {
      showNotification('Select at least one job', 'error')
      handleBulkDepartmentClose()
      return
    }

    if (!bulkDepartmentAction) {
      showNotification('Select at least one department', 'error')
      handleBulkDepartmentClose()
      return
    }

    setBulkDepartmentLoading(true)

    try {
      await bulkDepartmentUpdateMutation({ job_ids: selectedIds, department: bulkDepartmentAction })
    } catch {
      // Handled in onError
    } finally {
      setBulkDepartmentLoading(false)
    }
  }

  const handleCreateDrawerOpen = () => {
    setCreateDrawerOpen(true)
  }

  const handleInputChange = (value, field, mode = "create") => {
    if (mode === 'create') {
      setNewJob(prev => ({ ...prev, [field]: value }))
    } else {
      setFetchedJob(prev => ({ ...prev, [field]: value }))
    }
  }

  const handleRestaurantGroup = (ids) => {
    setCreateRestaurantGroupIds(ids)
  }

  const handleEditJob = (job) => {
    setFetchedJob({
      ...job,
      original_title: job?.title,
      tipped: job?.tipped?.toString(),
      cashier: job?.cashier?.toString(),
      driver: job?.driver?.toString()
    });
    setEditDrawerOpen(true)
  }

  const handleDeleteJobClick = (job) => {
    setDeleteTargetJob(job)
    setDeleteJobDialogOpen(true)
  }

  const canEditJob = (record) => {
    return getPermission(record, 'update_network_guest') && record?.source_of_truth === 'manual'
  }

  const canDeleteJob = (record) => {
    return getPermission(record, 'delete_network_guest') && record?.source_of_truth === 'manual'
  }

  const TableColumns = [
    { key: "id", sortable: true },
    { key: "applies_to", sortable: true },
    { key: "job_title", sortable: true },
    { key: "job_code", sortable: true },
    { key: "pay_basis", sortable: true },
    { key: "department", sortable: true },
    { key: "default_wage", sortable: true },
    { key: "tipped?", sortable: true },
    { key: "cashier?", sortable: true },
    { key: "driver?", sortable: true },
    { key: "exclude_from_reporting?", sortable: true },
    { key: "created_at", sortable: true },
    { key: "source_of_truth", sortable: true },
    { key: "actions", sortable: false }
  ];

  const getSortKeyFromColumnKey = (columnKey) => {
    switch (columnKey) {
      case 'job_title':
        return 'title'
      case 'job_code':
        return 'code'
      case 'tipped?':
        return 'tipped'
      case 'cashier?':
        return 'cashier'
      case 'driver?':
        return 'driver'
      case 'exclude_from_reporting?':
        return 'exclude_from_reporting'
      default:
        return columnKey
    }
  }

  const handleSortChange = (columnKey, direction) => {
    setSortBy(getSortKeyFromColumnKey(columnKey))
    setSortOrder(direction)
    setPage(1)
    setSelectedIds([])
  }

  const columns = TableColumns?.map((column) => {
    return {
      key: column?.key,
      label: camelize(column?.key),
      sortable: column.sortable,
    };
  });

  const TableRows = (data) => {
    return data?.map((record) => {
      return {
        id: record?.id,
        applies_to: record?.applies_to,
        job_title: {
          display: (
            <StyledNavLink 
              to={`/admin/jobs/${record.id}`}
              state={{ parentContext: { appliedFilters, filters, search, itemsPerPage, page, sortBy, sortOrder }, restaurant: navigatedRestaurant }}
            >
              {record?.title}
            </StyledNavLink>
          ),
          sortValue: record?.title?.toLowerCase()
        },
        job_code: record?.code,
        pay_basis: record?.pay_basis,
        department: {
          display: (
            <StyledStatusButton
              disableRipple
              data-testid={`department-button-${record?.id}`}
              endIcon={!canEditDepartment(record) ? null : <KeyboardArrowDownIcon sx={{ color: 'grey' }} />}
              onClick={(event) => handleDepartmentMenuOpen(event, record)}
              disabled={!canEditDepartment(record) || rowDepartmentLoadingId === record?.id}
            >
              <DepartmentBadge department={record?.department}>
                {DEPARTMENT_LABELS[record?.department] || DEPARTMENT_LABELS.unmapped}
              </DepartmentBadge>
            </StyledStatusButton>
          ),
          sortValue: record?.department
        },
        default_wage: record?.default_wage ? `$${Number(record?.default_wage).toFixed(2) ?? '0.00'}` : "--",
        "tipped?": {
          display: TableCheckbox(record?.tipped),
          sortValue: record?.tipped,
        },
        "cashier?": {
          display: TableCheckbox(record?.cashier),
          sortValue: record?.cashier,
        },
        "driver?": {
          display: TableCheckbox(record?.driver),
          sortValue: record?.driver,
        },
        "exclude_from_reporting?": {
          display: TableCheckbox(record?.exclude_from_reporting),
          sortValue: record?.exclude_from_reporting,
        },
        created_at: formatDate(record?.created_at, record?.time_zone_abbr),
        source_of_truth: formatHelper(record?.source_of_truth),
        actions: (
          <Box>
            <Tooltip title="Edit Job" arrow >
              <span>
                <IconButton
                  disabled={!canEditJob(record)}
                  onClick={() => handleEditJob(record)}
                >
                  <ModeEditIcon
                    sx={{
                      color: canEditJob(record) ? "#707070" : "#e0e0e0",
                      cursor: "pointer",
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Delete Job" arrow >
              <span>
                <IconButton
                  disabled={!canDeleteJob(record)}
                  onClick={() => handleDeleteJobClick(record)}
                >
                  <DeleteIcon
                    sx={{
                      color: canDeleteJob(record) ? "#707070" : "#e0e0e0",
                      cursor: "pointer",
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        )
      };
    });
  };

  const TableCheckbox = (checked) => {
    return (
      <Checkbox
        checked={checked}
        sx={{
          "&.Mui-checked": {
            color: "#2a4fb9",
          },
        }}
      />
    );
  };

  const rows = TableRows(jobs?.data);
  const hasSearchTerm = !!debouncedSearchTerm;
  const showInitialLoader = isFetching && !hasSearchTerm;

  const changePage = (nextPage) => {
    setPage(nextPage)
    setSelectedIds([])
  }

  const changeItemsPerPage = (nextItemsPerPage) => {
    setItemsPerPage(nextItemsPerPage)
    setSelectedIds([])
  }

  const jobsTableProps = {
    report: false,
    columns,
    tableId: "jobs",
    type: "entries",
    itemsPerPage,
    setItemsPerPage: changeItemsPerPage,
    page,
    setPage: changePage,
    searchLoading: isFetching && hasSearchTerm,
    hasSearchTerm,
    showDownloadButton: true,
    handleExportCsv,
    csvLoading,
    checkboxes: true,
    selectedIds,
    setSelectedIds,
  }

  const editableJobIds = new Set((jobs?.data || []).filter(canEditDepartment).map((job) => job.id))
  const canBulkUpdateDepartments = selectedIds.some((id) => editableJobIds.has(id))

  const { mutateAsync: uploadJobsMutation } = useOtMutation({
    mutationFn: async ({ file, restaurantId }) => await uploadJobs(file, restaurantId),
    invalidateKeys: [...jobMutationKeyGroups.upload],
  });

  const updateJobs = async (file, restaurantId) => {
    try {
      await uploadJobsMutation({ file, restaurantId });
      setRestaurantSelected(true)
    } catch (error) {
      console.error("Failed to fetch reservations:", error);
    } finally {
      setLoading(false);
      setFileUploadOpen(false);
    }
  };

  const handleUpload = async (file) => {
    // Simulate a file upload API call
    return new Promise((resolve) => {
      setTimeout(() => {
        console.log("Uploaded file:", file);
        updateJobs(file, restaurantId);
        resolve();
      }, 2000); // Simulate a delay for the upload
    });
  };

  const getRestaurantName = (id) => {
    if (!id) return ''

    if (Array.isArray(id)) {
      if (id.length === 0) return ''
      if (id.length === 1) {
        const restaurant = restaurantOptionsCache?.find((res) => `${res?.id}` === `${id?.[0]}`)
        return restaurant ? restaurant?.name : ''
      }
      return `${id.length} Restaurants Selected`
    }

    const restaurant = restaurantOptionsCache?.find((res) => `${res?.id}` === `${id}`)
    return restaurant ? restaurant?.name : ''
  }

  const getCreateRestaurantName = (id) => {
    const restaurant = createRestaurantOptionsCache?.find((res) => `${res?.value}` === `${id}`)
    return restaurant ? restaurant?.label : ''
  }

  const checkFetchPermission = (selectedRestaurantIds) => {
    if (superAdmin?.role === 'admin') {
      setFetchPermission(true)
      return
    }

    const allowed = (selectedRestaurantIds || [])
      .some((id) => strictCanScope(ability, 'fetch_and_imports', 'Restaurant', `${id}`))

    setFetchPermission(Boolean(allowed))
  }

  const checkCreateInstancePermission = (selectedRestaurantIds) => {
    if (superAdmin?.role === 'admin') {
      setCreateInstancePermission(true)
      return
    }

    const allowed = (selectedRestaurantIds || [])
      .some((id) => strictCanScope(ability, 'create_network_guest', 'Restaurant', `${id}`))

    setCreateInstancePermission(Boolean(allowed))
  }

  const handleDropDownChange = (event) => {
    const value = event.target.value || []
    setFiltersChanged(true);
    checkFetchPermission(value)
    setFilters(prev => ({ ...prev, restaurant_ids: value }))
    checkCreateInstancePermission(value)
  };

  const handleRestaurantGroupChange = (event) => {
    singleRestaurantAutoApplied.current = false
    setFilters(prev => ({ ...prev, restaurant_group_id: event.target.value, restaurant_ids: [] }))
    setRestaurantSearch('')
    setCreateInstancePermission(false)
    setFetchPermission(false)
    setFiltersChanged(true)
  }

  const handleRestaurantClear = () => {
    singleRestaurantAutoApplied.current = true
    setFilters(prev => ({ ...prev, restaurant_ids: [] }))
    setFiltersChanged(true)
    setFetchPermission(false)
    setCreateInstancePermission(false)
  }

  const handleRestaurantGroupClear = () => {
    singleRestaurantAutoApplied.current = true
    singleRestaurantGroupAutoApplied.current = true
    setFilters(prev => ({ ...prev, restaurant_group_id: null }))
    setFiltersChanged(true)
  }

  const handleReset = () => {
    setFiltersChanged(false);
    setApplyFilters(false);
    setShowResetAlert(true);
    setRestaurantId(null);
    setSearch('');
    setRestaurantSearch('');
    setRestaurantGroupSearch('');
    setSourceOfTruthSearch('');
    setRestaurantSelected(false);
    setFetchPermission(false);
    setCreateInstancePermission(false);
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setSortBy('created_at');
    setSortOrder('desc');
    setItemsPerPage(25);
    setPage(1);
    singleRestaurantAutoApplied.current = true;
    singleRestaurantGroupAutoApplied.current = true;
    setRestaurantOptionsCache([]);
  }

  const applySelectedFilters = (nextFilters) => {
    setAppliedFilters(nextFilters)
    setApplyFilters(true)
    setShowResetAlert(false);
    setFiltersChanged(false)
    setItemsPerPage(25)
    setPage(1)
    setSelectedIds([])
  }

  const handleChange = () => {
    if ((filters?.restaurant_group_id) || (filters?.restaurant_ids && filters?.restaurant_ids.length > 0)) {
      applySelectedFilters(filters)
    } else {
      showNotification('Please select a restaurant or restaurant group', 'error')
    }
    setFiltersChanged(false);
  };

  const handleConfiguredRestaurantFetch = () => {
    setEnableFetching(true)
  }

  const handleFetchModalClose = () => {
    setOpenFetchDataModal(false)
    setEnableFetching(false)
    setFetchSource({
      toast: false,
      seven_shifts: false
    })
  }

  const { toastConfiguredRestaurants, sevenShiftConfiguredRestaurants } = useMemo(() => {
    return bifurcateRestaurantData(configuredRestaurants);
  }, [configuredRestaurants]);

  const handleFetch = () => {
    handleFetchModalClose()
    fetchJobsFromToast()
  }

  return (
    <React.Fragment>
      <Box
        sx={{
          width: (createDrawerOpen || editDrawerOpen) ? `calc(100% - ${drawerWidth}px)` : "100%",
          overflowX: 'hidden',
        }}
      >
        <StyledBreadCrumbContainer>
          <Breadcrumbs>
            <NavigableBreadcrumbLink LinkComponent={StyledLink} label="Home" to="/admin/dashboard" />
            <StyledLink color={"#515152"}>Jobs</StyledLink>
          </Breadcrumbs>
        </StyledBreadCrumbContainer>

        <FilterContainer>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: "18px", flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", gap: "18px" }}>
              <Typography
                variant="body2"
                sx={{
                  ...viewTypographyStyles,
                  alignSelf: { xs: "flex-start", sm: "center" },
                  whiteSpace: "nowrap",
                  mt: 0
                }}
              >
                View
              </Typography>

              <FormControl fullWidth size="small">
                <StyledInputLabel>Restaurant Group</StyledInputLabel>
                <Select
                  size="small"
                  label="Restaurant Group"
                  data-testid="jobs-restaurant-group-filter"
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PaperProps: { ref: restaurantGroupMenuRef, sx: { maxHeight: "40vh" } },
                    autoFocus: false,
                    MenuListProps: { sx: { pt: 0 } },
                  }}
                  sx={{
                    ...selectBaseStyles,
                    minWidth: 160,
                    borderColor: "#2b4fb9",
                  }}
                  onChange={handleRestaurantGroupChange}
                  onClose={() => setTimeout(() => setRestaurantGroupSearch(''), 1000)}
                  value={filters?.restaurant_group_id || ""}
                  renderValue={() =>
                    getRestaurantGroupName(filters?.restaurant_group_id)
                  }
                  endAdornment={
                    filters?.restaurant_group_id ? (
                      <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                        <IconButton
                          edge='end'
                          size="small"
                          disableRipple
                          onClick={handleRestaurantGroupClear}
                        >
                          <ClearIcon sx={{ fontSize: '20px' }} />
                        </IconButton>
                      </InputAdornment>
                    ) : (
                      null
                    )
                  }
                >
                  <DropdownSearchBar changeSearchText={setRestaurantGroupSearch} placeholder={'Search restaurant group'} isFetching={restaurantGroupFetching} />
                  {
                    displayedRestaurantGroups
                      .map((res) => (
                        <StyledMenuItem
                          key={res?.label}
                          value={res?.label}
                        >
                          {
                            filters?.restaurant_group_id === res?.label ? <SelectedMenuItem data={res?.value} /> : res?.value
                          }
                        </StyledMenuItem>
                      ))
                  }
                  <DropdownInfiniteLoader
                    hasNextPage={hasNextRestaurantGroupsPage}
                    targetRef={restaurantGroupLoaderRef}
                  />
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <StyledInputLabel>Restaurant</StyledInputLabel>
                <Select
                  data-testid="job-restaurant-filter"
                  multiple
                  size="small"
                  label="Restaurant"
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PaperProps: { ref: restaurantMenuRef, sx: { maxHeight: "40vh" } },
                    autoFocus: false,
                    MenuListProps: { sx: { pt: 0 } },
                  }}
                  sx={{
                    ...selectBaseStyles,
                    minWidth: 160,
                    borderColor: "#2b4fb9",
                  }}
                  onChange={handleDropDownChange}
                  onClose={() => setTimeout(() => setRestaurantSearch(''), 1000)}
                  value={filters?.restaurant_ids || []}
                  renderValue={() =>
                    getRestaurantName(filters?.restaurant_ids)
                  }
                  endAdornment={
                    (filters?.restaurant_ids && filters?.restaurant_ids.length > 0) ? (
                      <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                        <IconButton
                          edge='end'
                          size="small"
                          disableRipple
                          onClick={handleRestaurantClear}
                        >
                          <ClearIcon sx={{ fontSize: '20px' }} />
                        </IconButton>
                      </InputAdornment>
                    ) : (
                      null
                    )
                  }
                >
                  <DropdownSearchBar changeSearchText={setRestaurantSearch} placeholder={'Search restaurant'} isFetching={restaurantFetching} />
                  {
                    displayedRestaurants
                      .map((res) => (
                        <StyledMenuItem
                          key={res?.id}
                          value={res?.id}
                        >
                          {
                            (filters?.restaurant_ids || []).includes(res?.id) ? <SelectedMenuItem data={res?.name} /> : res?.name
                          }
                        </StyledMenuItem>
                      ))
                  }
                  <DropdownInfiniteLoader
                    hasNextPage={hasNextRestaurantsPage}
                    targetRef={restaurantLoaderRef}
                  />
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <StyledInputLabel>Source of Truth</StyledInputLabel>
                <Select
                  data-testid='job-source-of-truth-filter'
                  value={filters?.source_of_truth || ''}
                  renderValue={() =>
                    formatHelper(filters?.source_of_truth)
                  }
                  size="small"
                  label="Source of Truth"
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                  }}
                  sx={{
                    ...selectBaseStyles,
                    minWidth: 160,
                    borderColor: "#2b4fb9",
                  }}
                  onChange={handleSourceOfTruthChange}
                  onClose={() => setTimeout(() => setSourceOfTruthSearch(''), 1000)}
                  endAdornment={
                    filters?.source_of_truth ? (
                      <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                        <IconButton
                          edge='end'
                          size="small"
                          disableRipple
                          onClick={handleSourceOfTruthClear}
                        >
                          <ClearIcon sx={{ fontSize: '20px' }} />
                        </IconButton>
                      </InputAdornment>
                    ) : (
                      null
                    )
                  }
                >
                  <DropdownSearchBar changeSearchText={setSourceOfTruthSearch} placeholder={'Search source of truth'} />
                  {
                    filteredSourceOfTruth
                      ?.filter((source_of_truth) =>
                        containsText(
                          `${source_of_truth?.label}`,
                          sourceOfTruthSearch
                        )
                      )
                      .map((item) => (
                        <StyledMenuItem
                          key={item?.value}
                          value={item?.value}
                        >
                          {
                            filters?.source_of_truth === item?.value ? <SelectedMenuItem data={item?.label} /> : item?.label
                          }
                        </StyledMenuItem>
                    ))
                  }
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <StyledInputLabel>Department</StyledInputLabel>
                <Select
                  data-testid='job-department-filter'
                  value={filters?.department || ''}
                  renderValue={() =>
                    DEPARTMENT_LABELS[filters?.department] || ''
                  }
                  size="small"
                  label="Department"
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                  }}
                  sx={{
                    ...selectBaseStyles,
                    minWidth: 160,
                    borderColor: "#2b4fb9",
                  }}
                  onChange={handleDepartmentChange}
                  onClose={() => setTimeout(() => setDepartmentSearch(''), 1000)}
                  endAdornment={
                    filters?.department ? (
                      <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                        <IconButton
                          edge='end'
                          size="small"
                          disableRipple
                          onClick={handleDepartmentClear}
                        >
                          <ClearIcon sx={{ fontSize: '20px' }} />
                        </IconButton>
                      </InputAdornment>
                    ) : (
                      null
                    )
                  }
                >
                  <DropdownSearchBar changeSearchText={setDepartmentSearch} placeholder={'Search department'} />
                  {
                    filteredDepartments
                      ?.map((item) => (
                        <StyledMenuItem
                          key={item?.value}
                          value={item?.value}
                        >
                          {
                            filters?.department === item?.value ? <SelectedMenuItem data={item?.label} /> : item?.label
                          }
                        </StyledMenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Box>
            
            <Box sx={{ display: "flex", gap: "10px" }}>
              <ResetButton
                onClick={handleReset}
                status={isFetching}
              />

              <UpdateButton
                onClick={handleChange}
                filtersChanged={filtersChanged}
                status={isFetching}
              />
            </Box>
            
          </Box>
        </FilterContainer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '18px', flexWrap: 'wrap', mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
            <Typography
              variant="body2"
              sx={{
                fontSize: '1.4rem',
                fontWeight: 600,
                padding: '22px 0px 10px 0px',
                whiteSpace: 'nowrap',
              }}
            >
              Jobs
            </Typography>
          </Box>

          <StyledSearchBar
            value={search}
            placeholder="Search job title or code"
            disabled={!hasAppliedRestaurantFilter}
            InputProps={{
              startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>),
            }}
            onChange={(e) => handleSearch(e.target.value)}
            autoComplete="off"
            inputProps={{ "data-testid": "jobs-search-input" }}
            sx={{ mt: '8px' }}
          />
        </Box>

        <Divider sx={{ borderStyle: 'solid', borderWidth: '1px', marginBottom: '25px' }} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <StyledCreateButton
            style={{ marginBottom: '10px' }}
            onClick={handleCreateDrawerOpen}
            variant="contained"
            startIcon={<AddIcon />}
            disabled={!(defaultCreatePermission || createInstancePermission)}
          >
            Add Job
          </StyledCreateButton>

            <ActionContainer sx={{ marginRight: "0px" }}>
            <ClassificationActionButton
              data-testid='department-action-btn'
              variant="contained"
              onClick={handleBulkDepartmentOpen}
              disabled={!canBulkUpdateDepartments || bulkDepartmentLoading}
            >
              Department Actions
            </ClassificationActionButton>

            {/* When requirement gets confirmed below code will be uncommented or removed */}

            {/* <FileUploadButton
              onUpload={handleUpload}
              open={fileUploadOpen}
              setOpen={setFileUploadOpen}
              loading={loading}
              setLoading={setLoading}
              uploadPermission={fetchPermission}
            /> */}

            <Button
              sx={{ background: "#2b4fb9" }}
              variant="contained"
              onClick={handleConfiguredRestaurantFetch}
              component="label"
              disabled={!fetchPermission || isLoading || configuredRestaurantsFetching}
              data-testid="fetch-jobs-button"
            >
              {isLoading || configuredRestaurantsFetching ? <LoadingSpinner isLoading={true} /> : <CachedIcon />}
            </Button>
          </ActionContainer>
        </div>

        {showResetAlert && !hasSearchTerm ? (
          <div>
            <Alert severity="info">Select a restaurant or restaurant group</Alert>
          </div>
        ) : showInitialLoader ? (
          <Loader />
        ) : jobs?.data? (
          <IndexTable
            {...jobsTableProps}
            data={rows}
            pageCount={jobs?.pageCount}
            disableDownloadButton={jobs?.data?.length === 0 || csvLoading}
            serverSort={true}
            onSortChange={handleSortChange}
            serverOrderBy={TableColumns.find((column) => getSortKeyFromColumnKey(column.key) === sortBy)?.key || ''}
            serverOrder={sortOrder}
          />
        ) : (
          <div>
            <Alert severity="info">Select a restaurant or restaurant group</Alert>
          </div>
        )}

        {/* Create Job Drawer */}
        <Drawer
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              marginTop: "calc(35px * 2)",
              display: "flex",
              flexDirection: "column",
            },
          }}
          variant="persistent"
          anchor="right"
          open={createDrawerOpen}
          data-testid="job-create-drawer"
        >
          <div
            style={{
              padding: "0px 15px",
              overflowY: "auto",
              flex: 1,
              marginBottom: "150px",
            }}
          >
            <StyledCreateTitle>Add Job</StyledCreateTitle>

            <div style={{ marginTop: '5%' }}>
              <StyledFormFieldText style={{ display: 'flex', alignItems: 'center' }}>
                {"Restaurant Group"}
                <Tooltip title="This filters the restaurants below" arrow placement="top">
                  <InfoCircleOutlined style={{ fontSize: '15px', marginLeft: '5px', marginTop: '-4px' }} />
                </Tooltip>
              </StyledFormFieldText>
              <Box>
                <StyledSelect
                  value={createRestaurantGroupIds || []}
                  onChange={(e) => handleRestaurantGroup(e.target.value)}
                  onClose={() => setTimeout(() => setCreateRestaurantGroupSearch(''), 1000)}
                  MenuProps={{
                    PaperProps: {
                      ref: createRestaurantGroupMenuRef,
                      sx: { maxHeight: 400 },
                    },
                    MenuListProps: { sx: { pt: 0 } },
                  }}
                  multiple
                  renderValue={(selected) =>
                    selected?.length < 1
                      ? ""
                      : selected?.length === 1
                        ? getCreateRestaurantGroupName(selected?.[0])
                        : `${selected?.length} Restaurant Groups Selected`
                  }
                >
                  <DropdownSearchBar changeSearchText={setCreateRestaurantGroupSearch} placeholder={'Search restaurant group'} isFetching={createRestaurantGroupFetching} />
                  {
                    displayedCreateRestaurantGroups
                      .map((res) => (
                        <StyledMenuItem
                          key={res?.label}
                          sx={{
                            fontSize: "0.875rem",
                          }}
                          value={res?.label}
                        >
                          {createRestaurantGroupIds?.includes(res?.label) ? <SelectedMenuItem data={res?.value} /> : res?.value}
                        </StyledMenuItem>
                      ))
                  }
                  <DropdownInfiniteLoader
                    hasNextPage={hasNextCreateRestaurantGroupsPage}
                    targetRef={createRestaurantGroupLoaderRef}
                  />
                </StyledSelect>
              </Box>
            </div>

            <div style={{ marginTop: "3%" }}>
              <StyledFormFieldText>
                Restaurant<span style={{ color: "red" }}>*</span>
              </StyledFormFieldText>
              <Box>
                <StyledSelect
                  value={newJob?.restaurant_id}
                  renderValue={() => getCreateRestaurantName(newJob?.restaurant_id)}
                  onChange={(e) => {
                    handleInputChange(e.target.value, "restaurant_id")
                    setErrors(prev => ({ ...prev, restaurant_id: null }))
                  }
                  }
                  onClose={() => setTimeout(() => setCreateRestaurantSearch(''), 1000)}
                  error={!!errors?.restaurant_id}
                  MenuProps={{
                    PaperProps: {
                      ref: createRestaurantMenuRef,
                      sx: { maxHeight: 400 },
                    },
                    MenuListProps: { sx: { pt: 0 } },
                  }}
                >
                  <DropdownSearchBar changeSearchText={setCreateRestaurantSearch} placeholder={'Search restaurant'} isFetching={createRestaurantFetching} />
                  {
                    displayedCreateRestaurants
                      ?.map((res) => (
                        <StyledMenuItem
                          key={res?.value}
                          value={res?.value}
                        >
                          {newJob?.restaurant_id === res?.value ? <SelectedMenuItem data={res?.label} /> : res?.label}
                        </StyledMenuItem>
                      ))
                  }
                  <DropdownInfiniteLoader
                    hasNextPage={hasNextCreateRestaurantsPage}
                    targetRef={createRestaurantLoaderRef}
                  />
                </StyledSelect>
                {!!errors?.restaurant_id && (
                  <FormHelperText
                    sx={{ color: "#d32f2f", marginLeft: "14px" }}
                  >
                    Please select a restaurant
                  </FormHelperText>
                )}
              </Box>
            </div>

            <div style={{ marginTop: "3%" }}>
              <StyledFormFieldText>
                Job Title<span style={{ color: "red" }}>*</span>
              </StyledFormFieldText>
              <StyledTextField
                fullWidth
                size="small"
                onChange={(e) => {
                  handleInputChange(e.target.value, "title")
                  setErrors(prev => ({ ...prev, title: null }))
                }
                }
                error={errors?.title}
                helperText={errors?.title}
                value={newJob?.title || ""}
                data-testid="create-title"
              />
            </div>

            <div style={{ marginTop: "3%" }}>
              <StyledFormFieldText>
                Job Code
              </StyledFormFieldText>
              <StyledTextField
                fullWidth
                size="small"
                onChange={(e) => {
                  handleInputChange(e.target.value, "code")
                  setErrors(prev => ({ ...prev, code: null }))
                }
                }
                error={errors?.code}
                helperText={errors?.code}
                value={newJob?.code || ""}
                data-testid="create-code"
              />
            </div>

            <FormControl sx={{ marginTop: '3%', display: 'block' }} variant="standard">
              <StyledFormFieldText style={{ marginBottom: '0px' }}>Pay Basis</StyledFormFieldText>
              <RadioGroup
                value={newJob?.pay_basis || ""}
                onChange={(e) => handleInputChange(e.target.value, "pay_basis")}
                sx={{ flexDirection: 'row', gap: '8px' }}
              >
                <FormControlLabel value="HOURLY" control={<Radio size="small" />} label="Hourly" />
                <FormControlLabel value="SALARY" control={<Radio size="small" />} label="Salary" />
              </RadioGroup>
            </FormControl>

            <div style={{ marginTop: "3%" }}>
              <StyledFormFieldText style={{ display: 'flex', alignItems: 'center' }}>
                {"Default Wage"}
                <Tooltip title="Must be a decimal" arrow placement="top">
                  <InfoCircleOutlined style={{ fontSize: '15px', marginLeft: '5px', marginTop: '-4px' }} />
                </Tooltip>
              </StyledFormFieldText>
              <StyledTextField
                fullWidth
                size="small"
                onChange={(e) => {
                  handleInputChange(e.target.value, "default_wage")
                  setErrors(prev => ({ ...prev, default_wage: null }))
                }
                }
                error={errors?.default_wage}
                helperText={errors?.default_wage}
                value={newJob?.default_wage || ""}
                data-testid="create-default-wage"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </div>

            <FormControl sx={{ marginTop: '3%', display: 'block' }} variant="standard">
              <StyledFormFieldText style={{ marginBottom: '0px' }}>Tipped?</StyledFormFieldText>
              <RadioGroup
                value={newJob?.tipped || ""}
                onChange={(e) => handleInputChange(e.target.value, "tipped")}
                sx={{ flexDirection: 'row', gap: '8px' }}
                data-testid="create-tipped"
              >
                <FormControlLabel value="true" control={<Radio size="small" />} label="Yes" />
                <FormControlLabel value="false" control={<Radio size="small" />} label="No" />
              </RadioGroup>
            </FormControl>

            <FormControl sx={{ marginTop: '3%', display: 'block' }} variant="standard">
              <StyledFormFieldText style={{ marginBottom: '0px' }}>Cashier?</StyledFormFieldText>
              <RadioGroup
                value={newJob?.cashier || ""}
                onChange={(e) => handleInputChange(e.target.value, "cashier")}
                sx={{ flexDirection: 'row', gap: '8px' }}
                data-testid="create-cashier"
              >
                <FormControlLabel value="true" control={<Radio size="small" />} label="Yes" />
                <FormControlLabel value="false" control={<Radio size="small" />} label="No" />
              </RadioGroup>
            </FormControl>

            <FormControl sx={{ marginTop: '3%' }} variant="standard">
              <StyledFormFieldText style={{ marginBottom: '0px' }}>Driver?</StyledFormFieldText>
              <RadioGroup
                value={newJob?.driver || ""}
                onChange={(e) => handleInputChange(e.target.value, "driver")}
                sx={{ flexDirection: 'row', gap: '8px' }}
                data-testid="create-driver"
              >
                <FormControlLabel value="true" control={<Radio size="small" />} label="Yes" />
                <FormControlLabel value="false" control={<Radio size="small" />} label="No" />
              </RadioGroup>
            </FormControl>

            <Paper
              sx={{
                position: "fixed",
                bottom: 0,
                right: 0,
                width: drawerWidth,
                height: "70px",
                zIndex: 1,
              }}
              elevation={2}
            >
              <StyledButtonContainer>
                <StyledCancelButton
                  onClick={handleCreateDrawerClose}
                  variant="outlined"
                >
                  Cancel
                </StyledCancelButton>
                <StyledAddButton
                  onClick={handleAddJob}
                  variant="contained"
                  disabled={loading}
                >
                  {loading ? <LoadingSpinner isLoading={true} /> : 'Add'}
                </StyledAddButton>
              </StyledButtonContainer>
            </Paper>
          </div>
        </Drawer>

        {/* Edit Job Drawer */}
        <Drawer
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              marginTop: "calc(35px * 2)",
              display: "flex",
              flexDirection: "column",
            },
          }}
          variant="persistent"
          anchor="right"
          open={editDrawerOpen}
          data-testid="job-edit-drawer"
        >
          <div
            style={{
              padding: "0px 15px",
              overflowY: "auto",
              flex: 1,
              marginBottom: "150px",
            }}
          >
            <StyledCreateTitle>Edit Job</StyledCreateTitle>

            <div style={{ marginTop: "3%" }}>
              <StyledFormFieldText>
                Restaurant
              </StyledFormFieldText>
              <StyledTextField
                fullWidth
                size="small"
                disabled={true}
                value={fetchedJob?.restaurant?.name}
              />
            </div>

            <div style={{ marginTop: "3%" }}>
              <StyledFormFieldText>
                Job Title<span style={{ color: "red" }}>*</span>
              </StyledFormFieldText>
              <StyledTextField
                fullWidth
                size="small"
                onChange={(e) => {
                  handleInputChange(e.target.value, "title", "edit")
                  setUpdateErrors(prev => ({ ...prev, title: null }))
                }
                }
                error={updateErrors?.title}
                helperText={updateErrors?.title}
                value={fetchedJob?.title || ""}
                data-testid="edit-title"
              />
            </div>

            <div style={{ marginTop: "3%" }}>
              <StyledFormFieldText>
                Job Code
              </StyledFormFieldText>
              <StyledTextField
                fullWidth
                size="small"
                onChange={(e) => {
                  handleInputChange(e.target.value, "code", "edit")
                  setUpdateErrors(prev => ({ ...prev, code: null }))
                }
                }
                error={updateErrors?.code}
                helperText={updateErrors?.code}
                value={fetchedJob?.code || ""}
                data-testid="edit-code"
              />
            </div>

            <FormControl sx={{ marginTop: '3%', display: 'block' }} variant="standard">
              <StyledFormFieldText style={{ marginBottom: '0px' }}>Pay Basis</StyledFormFieldText>
              <RadioGroup
                value={fetchedJob?.pay_basis ? capitalize(fetchedJob?.pay_basis) : ""}
                onChange={(e) => handleInputChange(e.target.value.toUpperCase(), "pay_basis", "edit")}
                sx={{ flexDirection: 'row', gap: '8px' }}
              >
                <FormControlLabel value="Hourly" control={<Radio size="small" />} label="Hourly" />
                <FormControlLabel value="Salary" control={<Radio size="small" />} label="Salary" />
              </RadioGroup>
            </FormControl>

            <div style={{ marginTop: "3%" }}>
              <StyledFormFieldText style={{ display: 'flex', alignItems: 'center' }}>
                {"Default Wage"}
                <Tooltip title="Must be a decimal" arrow placement="top">
                  <InfoCircleOutlined style={{ fontSize: '15px', marginLeft: '5px', marginTop: '-4px' }} />
                </Tooltip>
              </StyledFormFieldText>
              <StyledTextField
                fullWidth
                size="small"
                onChange={(e) => {
                  handleInputChange(e.target.value, "default_wage", "edit")
                  setUpdateErrors(prev => ({ ...prev, default_wage: null }))
                }
                }
                error={updateErrors?.default_wage}
                helperText={updateErrors?.default_wage}
                value={fetchedJob?.default_wage || ""}
                data-testid="edit-default-wage"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </div>

            <FormControl sx={{ marginTop: '3%', display: 'block' }} variant="standard">
              <StyledFormFieldText style={{ marginBottom: '0px' }}>Tipped?</StyledFormFieldText>
              <RadioGroup
                value={fetchedJob?.tipped || ""}
                onChange={(e) => handleInputChange(e.target.value, "tipped", "edit")}
                sx={{ flexDirection: 'row', gap: '8px' }}
                data-testid="edit-tipped"
              >
                <FormControlLabel value="true" control={<Radio size="small" />} label="Yes" />
                <FormControlLabel value="false" control={<Radio size="small" />} label="No" />
              </RadioGroup>
            </FormControl>

            <FormControl sx={{ marginTop: '3%', display: 'block' }} variant="standard">
              <StyledFormFieldText style={{ marginBottom: '0px' }}>Cashier?</StyledFormFieldText>
              <RadioGroup
                value={fetchedJob?.cashier || ""}
                onChange={(e) => handleInputChange(e.target.value, "cashier", "edit")}
                sx={{ flexDirection: 'row', gap: '8px' }}
                data-testid="edit-cashier"
              >
                <FormControlLabel value="true" control={<Radio size="small" />} label="Yes" />
                <FormControlLabel value="false" control={<Radio size="small" />} label="No" />
              </RadioGroup>
            </FormControl>

            <FormControl sx={{ marginTop: '3%' }} variant="standard">
              <StyledFormFieldText style={{ marginBottom: '0px' }}>Driver?</StyledFormFieldText>
              <RadioGroup
                value={fetchedJob?.driver || ""}
                onChange={(e) => handleInputChange(e.target.value, "driver", "edit")}
                sx={{ flexDirection: 'row', gap: '8px' }}
                data-testid="edit-driver"
              >
                <FormControlLabel value="true" control={<Radio size="small" />} label="Yes" />
                <FormControlLabel value="false" control={<Radio size="small" />} label="No" />
              </RadioGroup>
            </FormControl>

            <Paper
              sx={{
                position: "fixed",
                bottom: 0,
                right: 0,
                width: drawerWidth,
                height: "70px",
                zIndex: 1,
              }}
              elevation={2}
            >
              <StyledButtonContainer>
                <StyledCancelButton
                  onClick={handleEditDrawerClose}
                  variant="outlined"
                >
                  Cancel
                </StyledCancelButton>
                <StyledAddButton
                  onClick={handleUpdateJob}
                  variant="contained"
                  disabled={loading}
                >
                  {loading ? <LoadingSpinner isLoading={true} /> : 'Update'}
                </StyledAddButton>
              </StyledButtonContainer>
            </Paper>
          </div>
        </Drawer>

        <ConfirmationDialog
          open={deleteJobDialogOpen}
          onClose={() => setDeleteJobDialogOpen(false)}
          Icon={CheckIcon}
          dialogTitle="Are you sure you want to delete the job?"
          dialogBody={`This action will remove job: ${deleteTargetJob?.title
            ? `${deleteTargetJob?.title}`
            : "Unnamed Job"
            } from restaurant: ${deleteTargetJob?.restaurant?.name}`}
          confirmationText="Yes, I want to delete the job"
          handleConfirm={handleDeleteJob}
          isLoading={deleteLoading}
        />

        <ConfirmationDialog
          open={jobTitleConfirmationOpen}
          onClose={() => setJobTitleConfirmationOpen(false)}
          Icon={CheckIcon}
          dialogTitle="Are you sure you want to update the job title?"
          dialogBody={JOB_TITLE_UPDATE_CONFIRMATION_MESSAGE}
          confirmationText="Yes, continue updating the job title."
          handleConfirm={submitJobUpdate}
          isLoading={loading}
          dataTestId="job-title-confirmation-dialog"
        />

        <FetchDataModal
          open={openFetchDataModal}
          handleClose={handleFetchModalClose}
          modalTitle={'Sync Jobs'}
          toastConfiguredRestaurants={toastConfiguredRestaurants}
          sevenShiftConfiguredRestaurants={sevenShiftConfiguredRestaurants}
          totalRestaurantCount={configuredRestaurants?.length}
          fetchSource={fetchSource}
          setFetchSource={setFetchSource}
          handleFetch={handleFetch}
        />

        <Popover
          open={Boolean(departmentMenuAnchorEl)}
          anchorEl={departmentMenuAnchorEl}
          onClose={handleDepartmentMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{ sx: statusPopoverPaperStyle }}
          data-testid="department-menu-popover"
        >
          <StatusMenuList dense disablePadding>
            {DEPARTMENT_ACTIONS.map((action) => {
              const isCurrentDepartment = selectedDepartmentRecord?.department === action.value

              return (
                <StyledMenuItem
                  key={action.value}
                  disabled={isCurrentDepartment || rowDepartmentLoadingId === selectedDepartmentRecord?.id}
                  onClick={() => updateJobDepartment(selectedDepartmentRecord, action.value)}
                  sx={statusMenuItemStyle}
                >
                  {action.label}
                </StyledMenuItem>
              )
            })}
          </StatusMenuList>
        </Popover>

        {/* Bulk department classification */}
        <DiscountClassificationModal
          open={bulkDepartmentOpen}
          handleClose={handleBulkDepartmentClose}
          title="Department Classification"
          selectedAction={bulkDepartmentAction}
          setSelectedAction={setBulkDepartmentAction}
          onUpdate={updateJobDepartments}
          statusLoading={bulkDepartmentLoading}
          actions={DEPARTMENT_ACTIONS}
        />
      </Box>
    </React.Fragment>
  );
};
